import { Model } from '@vuex-orm/core';
import { createNamespacedHelpers } from 'vuex';

import ValidationMixin from '@/mixins/Validation.mixin.js';
import Role from '@/models/Role.js';
import Status from '@/models/Status.js';
import User from '@/models/User.js';
import UserGroup from '@/models/UserGroup.js';
import axios from '@/plugins/axios.js';
import Cache from '@/utilities/Cache.js';
import QueryBuilder from '@/utilities/QueryBuilder.js';

const { mapGetters } = createNamespacedHelpers('Groups');

export default {
    mixins: [ValidationMixin],

    data: () => ({
        newUserGroup: {
            group: null,
            role: null,
        },
        addingUserToGroup: false,
        savingUserGroup: false,
        deletingUserGroup: false,
        deletingUserAttribute: null,
        editingUserGroup: null,
        newRole: null,
        rolesAreLoading: false,
        selectedRow: false,
        viewComponent: null,
    }),

    computed: {
        user() {
            return User.query()
                .with([
                    'roles',
                    'enrollments.roles',
                    'enrollments.course.course_settings',
                    'enrollments.course.course_codes',
                    'enrollments.roles',
                    'enrollments.statuses',
                    'user_groups.role',
                    'user_groups.group',
                    'statuses',
                ])
                .find(this.userId || this.personId);
        },

        userStatus() {
            return Status.query()
                .where(status => {
                    return (
                        status.model_id === this.user.id &&
                        ['suspended', 'dismissed', 'withdrawn'].includes(status.name)
                    );
                })
                .get();
        },

        roles() {
            return Role.query().where('group_assignable', true).get();
        },

        ...mapGetters({ groupsAreLoading: 'isLoading' }),

        userModel() {
            return User.query()
                .with(['roles', 'enrollments.roles', 'user_groups.role'])
                .find(this.userId || this.personId);
        },
    },

    methods: {
        hasGroupPermissions(permission) {
            if (!this.userModel) return false;

            return this.userModel.hasGroupPermissionTo(permission);
        },

        editUserGroup(attribute) {
            this.newRole = attribute.role_id;
            this.editingUserGroup = attribute.id;
        },

        async saveUserGroup(attribute) {
            try {
                this.savingUserGroup = true;

                const queryBuilder = new QueryBuilder(`user-groups/${attribute.id}`).include(['group', 'role']);

                await UserGroup.api().put(
                    queryBuilder.toUrl(),
                    {
                        group_id: attribute.group_id,
                        role_id: this.newRole,
                    },
                    { errorHandle: false }
                );

                if (this.user && this.user.id === attribute.user_id) {
                    Cache.clear();
                }

                this.savingUserGroup = false;
                this.editingUserGroup = false;
            } catch (e) {
                this.scanValidationErrors(e);
            }
        },

        confirmDelete(attribute, callback) {
            this.$dialog.confirm({
                title: 'Remove Group Assignment',
                message: 'Are you sure you want to remove this group assignment?',
                confirmText: 'Remove',
                cancelText: 'Cancel',
                type: 'is-danger',
                onConfirm: async () => {
                    await this.deleteUserAttribute(attribute);

                    if (attribute instanceof Model) {
                        await attribute.$delete();
                    }

                    if (callback) {
                        callback();
                    }
                },
            });
        },

        async deleteUserAttribute(attribute) {
            try {
                this.deletingUserAttribute = attribute.id;

                await axios.delete(`/api/user-groups/${attribute.id}`, {
                    errorHandle: false,
                });

                await attribute.$delete();

                await User.query()
                    .with([
                        'roles',
                        'enrollments.course.course_settings',
                        'enrollments.roles',
                        'enrollments.statuses',
                        'user_groups.role',
                        'user_groups.group',
                        'statuses',
                    ])
                    .find(this.$route.params.personId);

                if (this.user?.id === attribute.user_id) {
                    Cache.clear();
                }

                this.selectedRow = false;
                this.deletingUserAttribute = null;
            } catch (e) {
                this.scanValidationErrors(e);
            }
        },

        cancelUserGroup() {
            this.resetEdit();
        },

        resetEdit() {
            this.newRole = null;
            this.editingUserGroup = null;
        },

        selectRow(row) {
            this.selectedRow = row.id;
        },

        isSaving(value) {
            this.savingUserGroup = value;
            this.addingUserToGroup = value;
        },
    },
};
